'use client'

import { cva } from 'class-variance-authority'

import { Check, CheckCircleOutline, Point, Warning } from '../../../icons'
import { cn } from '../../../utils/cn'
import { formatNumber } from '../../../utils/formatNumber'
import { BonusBadge } from '../BonusBadge'
import { FeeBreakdown } from '../FeeBreakdown'

export type PlanCardState = 'default' | 'selected' | 'unavailable'

const planCardVariants = cva(
  'relative flex w-full flex-col items-center rounded-md transition-all',
  {
    variants: {
      state: {
        default: 'border border-solid border-gray-04 bg-white',
        selected: 'border border-solid border-red-01 bg-red-04',
        // The unavailable state drops the border entirely (Figma: border-0)
        unavailable: 'border-0 bg-white',
      },
      // Subscription cards follow the 2026-08 Figma update. The まとめ買い /
      // 都度払い cards on the spot tab are outside that update's scope and keep
      // their previous spacing.
      layout: {
        subscription: 'gap-4 px-4 py-6',
        spot: 'gap-2 p-4',
      },
    },
    defaultVariants: {
      state: 'default',
      layout: 'subscription',
    },
  },
)

// Spacing that differs between the two layouts below the root element
const layoutClasses = {
  subscription: {
    check: 'left-2 top-2',
    pointBlock: 'w-full px-2',
    pointRow: 'gap-1',
  },
  spot: {
    check: 'left-4 top-4',
    pointBlock: 'px-4',
    pointRow: 'gap-2',
  },
} as const

export interface PlanCardProps {
  /**
   * Class name
   */
  className?: string

  /**
   * Which kind of purchase this card represents
   */
  type: 'subscription' | 'bulk' | 'shortage'

  /**
   * Visual state
   */
  state?: PlanCardState

  /**
   * Tagline. Subscription cards render it under the point block, where `\n`
   * breaks the line; まとめ買い / 都度払い cards render it as the pill badge
   * at the top.
   */
  description?: string

  /**
   * Card title (plan name / まとめ買い / 都度払い)
   */
  title: string

  /**
   * Price in yen
   */
  price: number

  /**
   * Unit suffix rendered next to the price (e.g. `/月`)
   */
  priceUnit: string

  /**
   * Total points granted, shown in the point badge
   */
  point: number

  /**
   * Bonus rate in percent. Above 0 the 「25％おまけ」 badge is rendered in the
   * point block.
   */
  bonusRate?: number

  /**
   * Ribbon rendered on the top edge of the card (e.g. 人気No.1)
   */
  label?: string

  /**
   * Reserves the ribbon's space at the top even without a `label`, so the
   * titles of cards in the same row line up with a labelled neighbour
   */
  reserveLabelSpace?: boolean

  /**
   * Small payment handling fee. When above 0 the price is rendered as a
   * breakdown of shortage + fee instead of a single amount.
   */
  transactionFee?: number

  /**
   * Notice rendered at the bottom while the card is unavailable
   */
  unavailableNote?: string

  onClick?: () => void
}

export const PlanCard = ({
  className,
  type,
  state = 'default',
  description,
  title,
  price,
  priceUnit,
  point,
  bonusRate = 0,
  label,
  reserveLabelSpace,
  transactionFee = 0,
  unavailableNote,
  onClick,
}: PlanCardProps) => {
  const unavailable = state === 'unavailable'
  const isSubscription = type === 'subscription'
  const layout = isSubscription ? 'subscription' : 'spot'
  const classes = layoutClasses[layout]

  const accentText = unavailable ? 'text-gray-01' : 'text-blue-01'
  const pointText = unavailable ? 'text-gray-01' : 'text-red-01'
  const pointIconBg = unavailable ? 'bg-gray-02' : 'bg-red-01'
  const bodyText = unavailable ? 'text-gray-01' : 'text-black'

  const showFeeBreakdown = type === 'shortage' && transactionFee > 0

  return (
    <button
      type="button"
      className={cn(
        planCardVariants({ state, layout }),
        // Ribbon height plus breathing room, as in Figma (pt-36)
        isSubscription && (label || reserveLabelSpace) && 'pt-9',
        className,
      )}
      // An unavailable card is not a toggle, so it carries no pressed state
      aria-pressed={unavailable ? undefined : state === 'selected'}
      disabled={unavailable}
      onClick={onClick}
    >
      {label && (
        // -top-px covers the card border so the ribbon sits flush on the edge
        <span className="absolute -top-px left-1/2 -translate-x-1/2 whitespace-nowrap rounded-b-md bg-gradient-primary px-4 py-1 text-white typography-subhead3">
          {label}
        </span>
      )}

      {state === 'selected' && (
        <span
          className={cn(
            'absolute flex size-8 items-center justify-center rounded-full bg-red-01',
            classes.check,
          )}
        >
          <Check className="size-5 text-white" />
        </span>
      )}

      {state === 'default' && (
        <CheckCircleOutline
          className={cn('absolute size-8 text-gray-05', classes.check)}
        />
      )}

      <div className="flex w-full flex-col items-center justify-center gap-2">
        {!isSubscription && description && (
          <div
            className={cn(
              'rounded-full border border-solid border-gray-04 bg-white px-3 py-1 typography-caption1',
              bodyText,
            )}
          >
            {description}
          </div>
        )}

        <div className={cn('text-center typography-subhead1', accentText)}>
          {title}
        </div>

        {!showFeeBreakdown && (
          <div className={cn('flex items-end gap-1', accentText)}>
            <span className="typography-subhead1">{formatNumber(price)}円</span>
            <span className="typography-body2">{priceUnit}</span>
          </div>
        )}

        {showFeeBreakdown && (
          <FeeBreakdown value={price} transactionFee={transactionFee} />
        )}

        <div
          className={cn(
            'flex flex-col items-center gap-1.5 rounded-sm bg-gray-06 py-[7px]',
            classes.pointBlock,
          )}
        >
          <div
            className={cn('flex items-center justify-center', classes.pointRow)}
          >
            <span
              className={cn(
                'flex size-6 items-center justify-center rounded-full',
                pointIconBg,
              )}
            >
              <Point className="size-4 text-white" />
            </span>

            <span className={cn('typography-subhead1', pointText)}>
              {formatNumber(point)}pt
            </span>

            <span className={cn('typography-body2', accentText)}>GET!</span>
          </div>

          {bonusRate > 0 && (
            <BonusBadge
              rate={bonusRate}
              className={cn(unavailable && 'border-gray-02 text-gray-01')}
            />
          )}
        </div>
      </div>

      {isSubscription && description && (
        // pre-line: the line breaks are set in the data, as Figma breaks the
        // taglines at phrase boundaries
        <div
          className={cn(
            'whitespace-pre-line text-center typography-subhead3',
            bodyText,
          )}
        >
          {description}
        </div>
      )}

      {unavailable && unavailableNote && (
        <div className="flex w-full items-center justify-center gap-2 bg-red-04 px-2 py-1.5">
          <Warning className="size-4 shrink-0 text-red-01" />
          <span className="text-red-01 typography-subhead3">
            {unavailableNote}
          </span>
        </div>
      )}
    </button>
  )
}
